import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { formatAgeRange, formatDateRange, stripAdventureMeta } from "@/lib/adventure-helpers";
import {
  isRecentMemberActivity,
} from "../../shared/activities";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type AdventureRow = Tables<"adventures"> & {
  profiles?: {
    display_name: string | null;
    postcode: string | null;
    avatar_url: string | null;
  } | null;
};

type AdminAdventureRow = AdventureRow & {
  interested_count: number;
  going_count: number;
  is_recent_submission: boolean;
};

const formatCreatedLabel = (value: string | null | undefined) => {
  if (!value) return "Added recently";
  return `Added ${new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  })}`;
};

const truncate = (value: string | null | undefined, maxLength = 180) => {
  if (!value) return "";
  return value.length <= maxLength ? value : `${value.slice(0, maxLength - 1).trim()}…`;
};

const AdminActivities = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<AdminAdventureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const { data: rows, error } = await supabase
      .from("adventures")
      .select("*, profiles:profiles!adventures_user_id_fkey(display_name, postcode, avatar_url)")
      .eq("status", "active")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Could not load member activities", error);
      toast.error("Could not load member activities");
      setLoadError(error.message || "Please try again.");
      setLoading(false);
      return;
    }

    const adventureIds = (rows ?? []).map((activity) => activity.id);
    const { data: memberRows, error: memberError } = adventureIds.length
      ? await supabase.from("adventure_members").select("adventure_id, status").in("adventure_id", adventureIds)
      : { data: [] as Array<{ adventure_id: string; status: string }>, error: null };

    if (memberError) {
      console.error("Could not load activity counts", memberError);
      toast.error("Could not load activity counts");
      setLoadError(memberError.message || "Please try again.");
      setLoading(false);
      return;
    }

    const countMap: Record<string, { interested: number; going: number }> = {};

    for (const member of memberRows ?? []) {
      if (!countMap[member.adventure_id]) {
        countMap[member.adventure_id] = { interested: 0, going: 0 };
      }

      if (member.status === "interested") {
        countMap[member.adventure_id].interested += 1;
      }

      if (member.status === "joined") {
        countMap[member.adventure_id].going += 1;
      }
    }

    setActivities(
      ((rows ?? []) as AdventureRow[]).map((activity) => ({
        ...activity,
        description: stripAdventureMeta(activity.description),
        interested_count: countMap[activity.id]?.interested ?? 0,
        going_count: countMap[activity.id]?.going ?? 0,
        is_recent_submission: isRecentMemberActivity(activity.created_at),
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadActivities();
  }, [loadActivities]);

  const recentCount = useMemo(
    () => activities.filter((activity) => activity.is_recent_submission).length,
    [activities],
  );

  const removeActivity = async (activity: AdminAdventureRow) => {
    if (!window.confirm(`Remove "${activity.title}" from members now?`)) {
      return;
    }

    setRemovingId(activity.id);
    const { error } = await supabase
      .from("adventures")
      .update({ status: "inactive" })
      .eq("id", activity.id);
    setRemovingId(null);

    if (error) {
      toast.error(error.message || "Could not remove activity");
      return;
    }

    toast.success("Activity removed from members");
    await loadActivities();
  };

  return (
    <AdminPageScaffold
      title="Member activities"
      subtitle="Activities go live immediately for members, and new ones land here too so you can review the wording, amend details, or remove anything that should not stay visible."
      fallbackPath="/admin"
    >
      {loading ? (
        <AppLoadingState
          title="Loading member activities"
          body="Pulling together the newest live activities and organiser details."
        />
      ) : loadError ? (
        <AppEmptyState
          title="Member activities could not load"
          body={loadError}
          action={<AppButton type="button" tone="outline" onPress={() => void loadActivities()}>Try again</AppButton>}
        />
      ) : (
        <>
          <AppCard>
            <AppCardHeader>
              <AppSectionHeader
                title="Queue overview"
                subtitle="Recent activity creations are counted as the items most likely to need your eye first, while the full list below stays available for later tidy-up."
              />
            </AppCardHeader>
            <AppCardBody>
              <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$hero" lineHeight="$hero">
                {recentCount}
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {recentCount === 1
                  ? "1 newly created activity is live and ready for a quick admin check."
                  : `${recentCount} newly created activities are live and ready for a quick admin check.`}
              </AppText>
            </AppCardBody>
          </AppCard>

          {activities.length === 0 ? (
            <AppEmptyState
              title="No live member activities"
              body="When members create activities, they will show up here for a quick admin pass."
            />
          ) : (
            <AppCard>
              <AppCardHeader>
                <AppSectionHeader
                  title="Newest live activities"
                  subtitle="Open the activity, amend it with the shared editor, or remove it from members if it is not right for Kindred."
                />
              </AppCardHeader>
              <AppCardBody gap="$md">
                {activities.map((activity) => (
                  <AppCard key={activity.id} backgroundColor="$background">
                    <AppCardBody gap="$sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$md" lineHeight="$md">
                          {activity.title}
                        </AppText>
                        {activity.is_recent_submission ? (
                          <AdminStatusPill tone="primary">New this week</AdminStatusPill>
                        ) : null}
                        <AdminStatusPill tone="secondary">{activity.type}</AdminStatusPill>
                      </div>

                      <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                        Organised by {activity.profiles?.display_name ?? "Kindred member"}
                        {activity.profiles?.postcode ? ` · ${activity.profiles.postcode}` : ""}
                        {` · ${formatCreatedLabel(activity.created_at)}`}
                      </AppText>

                      <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                        {formatDateRange(activity.date_start, activity.date_end)} · {activity.location} ·{" "}
                        {formatAgeRange(activity.min_child_age, activity.max_child_age)} · Up to {activity.max_group_size} families
                      </AppText>

                      <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                        {activity.interested_count} interested · {activity.going_count} going
                      </AppText>

                      {activity.description ? (
                        <AppText fontSize="$sm" lineHeight="$md">
                          {truncate(activity.description)}
                        </AppText>
                      ) : null}

                      <AppButtonRow justifyContent="flex-start">
                        <AppButton type="button" tone="outline" size="sm" onPress={() => navigate(`/adventures/${activity.id}`)}>
                          Open
                        </AppButton>
                        <AppButton type="button" tone="secondary" size="sm" onPress={() => navigate(`/adventures/edit/${activity.id}`)}>
                          Edit
                        </AppButton>
                        <AppButton
                          type="button"
                          tone="danger"
                          size="sm"
                          disabled={removingId === activity.id}
                          onPress={() => void removeActivity(activity)}
                        >
                          {removingId === activity.id ? "Removing..." : "Remove"}
                        </AppButton>
                      </AppButtonRow>
                    </AppCardBody>
                  </AppCard>
                ))}
              </AppCardBody>
            </AppCard>
          )}
        </>
      )}
    </AdminPageScaffold>
  );
};

export default AdminActivities;
